import { memo } from 'react'
import { motion, type MotionValue, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { HatGlasses, History, Menu } from 'lucide-react'
import { Interactive } from '@highlight/ui'
import { SIDEPANEL_SIDES, type SidepanelSide } from '@shared/sidepanel/side'
import { sidepanelVisibleWidth } from '../../../atoms'
import { SidepanelSection } from './section'

const ZONE_INSET = 16

const ZONE_VARIANTS: Variants = {
  initial: { opacity: 0 },
  hovered: { opacity: 1, scale: 1 },
  idle: { opacity: 1, scale: 0.99 },
}

const ZONE_TRANSITION: Transition = { duration: 0.15, ease: 'easeOut' }

interface SidepanelDragDropZonesProps {
  hoveredSide: SidepanelSide
  sidepanelWidth: number
  /** Top of the panel's vertical band, so the previews clear the menu bar like the panel does. */
  bandTop: number
  pointerX: MotionValue<number>
  pointerY: MotionValue<number>
  /** Show the cursor-following toolbar ghost. */
  showGhost: boolean
}

/**
 * Drag feedback shown while the sidepanel is being repositioned: one rounded
 * rect per screen edge previewing where the panel's visible content will land.
 * The active target is solid and filled; the other side stays clearly visible
 * but dashed, so on wide displays both options read immediately.
 *
 * A cursor-following toolbar ghost makes the drag obvious even when the panel
 * cannot move vertically.
 *
 * Rendered outside the panel's transformed root (fixed positioning inside a
 * transform would resolve against the panel, not the screen). The full-screen
 * `Interactive` keeps the overlay window interactive for the whole drag,
 * wherever the cursor goes, and the layer itself takes pointer events so
 * nothing underneath (list rows, hover cards) reacts to the passing cursor.
 */
export const SidepanelDragDropZones = memo(function SidepanelDragDropZones({
  hoveredSide,
  sidepanelWidth,
  bandTop,
  pointerX,
  pointerY,
  showGhost,
}: SidepanelDragDropZonesProps) {
  // Preview the panel's visible content region, not its full logical width —
  // including the gutter makes the zone read wider than the panel actually looks.
  const zoneWidth = sidepanelVisibleWidth(sidepanelWidth) - ZONE_INSET

  return (
    <div className="pointer-events-auto fixed inset-0 z-50">
      <Interactive />
      {SIDEPANEL_SIDES.map((zone) => {
        const isHovered = hoveredSide === zone
        return (
          <motion.div
            key={zone}
            variants={ZONE_VARIANTS}
            initial="initial"
            animate={isHovered ? 'hovered' : 'idle'}
            transition={ZONE_TRANSITION}
            className={twMerge(
              'corner-smooth absolute rounded-2xl border-2',
              isHovered
                ? 'border-white-11 bg-on-dark-subtle-active'
                : 'border-white-10 bg-on-dark-subtle border-dashed',
            )}
            style={{
              top: bandTop + ZONE_INSET,
              bottom: ZONE_INSET,
              width: zoneWidth,
              [zone]: ZONE_INSET,
            }}
          />
        )
      })}
      {showGhost && (
        <motion.div className="absolute left-0 top-0 will-change-transform" style={{ x: pointerX, y: pointerY }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
            className="-translate-x-1/2 translate-y-4"
          >
            <SidepanelSection className="bg-background/95 corner-smooth w-80 flex-row items-center justify-end gap-1 rounded-xl p-2">
              <span className="text-foreground-secondary flex size-[26px] items-center justify-center">
                <History className="size-3.5" />
              </span>
              <span className="text-foreground-secondary flex size-[26px] items-center justify-center">
                <HatGlasses className="size-3.5" />
              </span>
              <span className="text-foreground-secondary flex size-[26px] items-center justify-center">
                <Menu className="size-3.5" />
              </span>
            </SidepanelSection>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
})
